/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Search, X, Plus, Filter, Loader2, Check, ChevronsUpDown } from "lucide-react"
import { PageHeader } from "@/components/Molecules/PageHeader/PageHeader"
import PageMainContainer from "@/components/Templates/PageMainContainer"
import { RepriseCard } from "../components/RepriseCard"
import { RepriseSkeleton } from "../components/RepriseSkeleton"
import {
  getReprisesAction,
  getImersoes,
  updateContent,
  deleteContent,
  Evento,
  createContent,
} from "@/modules/Reprises/service/ReprisesService"
import { useLoading } from "@/hooks/useLoading"
import { Button } from "@/components/ui/button"
import { useToast } from "@/components/ui/use-toast"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { isPast, format } from "date-fns"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { ReprisesFilter } from "../components/ReprisesFilter"

const COLOR_SEQUENCE = [
  {
    accent: "text-orange-500",
    border: "border-orange-500/20",
    bg: "from-orange-500/[0.04]",
    button: "border-orange-500/20 text-orange-500 hover:bg-orange-500",
  },
  {
    accent: "text-blue-500",
    border: "border-blue-500/20",
    bg: "from-blue-500/[0.04]",
    button: "border-blue-500/20 text-blue-500 hover:bg-blue-500",
  },
  {
    accent: "text-emerald-500",
    border: "border-emerald-500/20",
    bg: "from-emerald-500/[0.04]",
    button: "border-emerald-500/20 text-emerald-500 hover:bg-emerald-500",
  },
  {
    accent: "text-purple-500",
    border: "border-purple-500/20",
    bg: "from-purple-500/[0.04]",
    button: "border-purple-500/20 text-purple-500 hover:bg-purple-500",
  },
  {
    accent: "text-rose-500",
    border: "border-rose-500/20",
    bg: "from-rose-500/[0.04]",
    button: "border-rose-500/20 text-rose-500 hover:bg-rose-500",
  },
]

const formSchema = z.object({
  id: z.string().min(1, { message: "Selecione um evento." }),
})

export default function ReprisesPage() {
  const { hideLoading } = useLoading()
  const { toast } = useToast()
  const { user } = useAuth()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: "",
    },
  })

  // Estados principais
  const [eventos, setEventos] = useState<Evento[]>([])
  const [categorias, setCategorias] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<"all" | "ativos" | "expirados">("all")
  const [isLoading, setIsLoading] = useState(true)
  const [currentEditingEventId, setCurrentEditingEventId] = useState<number | null>(null)

  // Estados de Edição/Criação
  const [editingEvent, setEditingEvent] = useState<any | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [dateInput, setDateInput] = useState("")
  const [timeInput, setTimeInput] = useState("")
  const [capaInput, setCapaInput] = useState("")
  const [videoInput, setVideoInput] = useState("")
  const [videoSecundarioInput, setVideoSecundarioInput] = useState("")

  // Estados do Agrupador (Tags) - NOVO COMPORTAMENTO
  const [agrupadorInput, setAgrupadorInput] = useState("")
  const [isAgrupadorOpen, setIsAgrupadorOpen] = useState(false)
  const [searchAgrupador, setSearchAgrupador] = useState("")
  const agrupadorRef = useRef<HTMLDivElement>(null)

  const [listaImersoes, setListaImersoes] = useState<any[]>([])
  const [activePlayback, setActivePlayback] = useState<{ title: string; links: string[] } | null>(
    null
  )

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 500)
    return () => clearTimeout(handler)
  }, [searchTerm])

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (agrupadorRef.current && !agrupadorRef.current.contains(e.target as Node))
        setIsAgrupadorOpen(false)
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  const getEmbedUrl = (url: string) => {
    if (!url) return ""
    if (url.includes("youtube.com/embed/")) return url
    const videoId = url.split("v=")[1]?.split("&")[0] || url.split("youtu.be/")[1]
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url
  }

  const init = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await getReprisesAction({
        page: 1,
        limit: 100,
        search: debouncedSearch,
        status: statusFilter !== "all" ? statusFilter : undefined,
      })
      const data = response.data.data || []
      setEventos(data)
      const rawCats = Array.from(
        new Set(
          data.map((e: any) =>
            (e.agrupador || "OUTROS_CONTEUDOS").trim().toUpperCase().replace(/\s+/g, "_")
          )
        )
      ) as string[]
      setCategorias(
        rawCats.sort((a, b) => {
          const clean = (s: string) => s.toLowerCase().replace(/_/g, " ").trim()
          if (clean(a) === "sala de guerra") return -1
          if (clean(b) === "sala de guerra") return 1
          return a.localeCompare(b)
        })
      )
    } catch (error) {
      toast({ variant: "destructive", title: "Erro ao carregar dados" })
    } finally {
      setIsLoading(false)
      hideLoading()
    }
  }, [debouncedSearch, statusFilter, hideLoading, toast])

  useEffect(() => {
    init()
  }, [init])

  useEffect(() => {
    const loadImersoes = async () => {
      try {
        const res = await getImersoes({ page: 1, limit: 1000 })
        setListaImersoes(res.data.data || [])
      } catch (e) {
        toast({ variant: "destructive", title: "Erro ao carregar imersões" })
      }
    }
    loadImersoes()
  }, [toast])

  const handleDelete = async (id: string | number) => {
    try {
      await deleteContent(id)
      toast({ title: "Conteúdo excluído!" })
      init()
    } catch (error) {
      toast({ variant: "destructive", title: "Erro ao excluir." })
    }
  }

  const handleDateChange = (e: any) => {
    let val = e.target.value.replace(/\D/g, "").slice(0, 8)
    if (val.length >= 5) val = val.replace(/^(\d{2})(\d{2})(\d{0,4}).*/, "$1/$2/$3")
    else if (val.length >= 3) val = val.replace(/^(\d{2})(\d{0,2}).*/, "$1/$2")
    setDateInput(val)
  }

  const handleTimeChange = (e: any) => {
    let val = e.target.value.replace(/\D/g, "").slice(0, 4)
    if (val.length >= 3) val = val.replace(/^(\d{2})(\d{0,2}).*/, "$1:$2")
    setTimeInput(val)
  }

  useEffect(() => {
    if (editingEvent) {
      setCapaInput(editingEvent.linkCapa || "")
      setVideoInput(editingEvent.linkConteudo || "")
      setVideoSecundarioInput(editingEvent.linkSegundoConteudo || "")
      setAgrupadorInput(editingEvent.agrupador || "")

      if (editingEvent.idEvento) {
        form.setValue("id", editingEvent.idEvento?.toString() || "")
      } else {
        form.reset({ id: "" })
      }

      if (editingEvent.limiteExibicao && editingEvent.idEvento) {
        const d = new Date(editingEvent.limiteExibicao)
        setDateInput(format(d, "dd/MM/yyyy"))
        setTimeInput(format(d, "HH:mm"))
      } else {
        setDateInput("")
        setTimeInput("")
      }
    }
  }, [editingEvent, form])

  const handleFormSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSaving(true)
    try {
      const isEditing = !!editingEvent?.idEvento
      const formattedAgrupador = agrupadorInput.trim().toUpperCase().replace(/\s+/g, "_")
      const [d, m, y] = dateInput.split("/")
      const [h, min] = timeInput.split(":")

      const payload = {
        idEvento: parseInt(values.id, 10),
        agrupador: formattedAgrupador,
        linkConteudo: videoInput,
        linkSegundoConteudo: videoSecundarioInput,
        linkCapa: capaInput,
        limiteExibicao: `${y}-${m}-${d}T${h}:${min}:00`,
      }

      if (isEditing) {
        await updateContent({ ...payload, idEventoAtual: currentEditingEventId })
      } else {
        await createContent(payload)
      }

      toast({ title: "Sucesso!" })
      setEditingEvent(null)
      setAgrupadorInput("")
      init()
    } catch (e) {
      toast({ variant: "destructive", title: "Erro ao salvar" })
    } finally {
      setIsSaving(false)
    }
  }

  const isFormValid = useMemo(
    () =>
      dateInput.length === 10 &&
      timeInput.length === 5 &&
      capaInput.trim() !== "" &&
      videoInput.trim() !== "" &&
      agrupadorInput.trim() !== "" &&
      !!form.watch("id"),
    [dateInput, timeInput, capaInput, videoInput, agrupadorInput, form]
  )

  return (
    <div className="min-h-screen bg-zinc-950 pb-32 text-zinc-100">
      <div className="flex flex-col items-start justify-between gap-4 px-6 md:flex-row md:items-center md:px-10">
        <PageHeader
          title="Conteúdos"
          icon="clapperboard-play"
        />
        {user.gestaoApp && (
          <Button
            onClick={() => {
              setSearchTerm("")
              setEditingEvent({ id: null, descricao: "Novo Conteúdo" })
            }}
            className="h-14 rounded-2xl bg-orange-600 px-8 text-xs font-black uppercase tracking-widest text-white shadow-lg transition-all hover:scale-105 hover:bg-orange-600"
          >
            <Plus
              size={18}
              className="mr-2"
            />{" "}
            Novo
          </Button>
        )}
      </div>

      <PageMainContainer>
        <div className="mb-12">
          <ReprisesFilter
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            isAdmin={!!user.gestaoApp}
          />
        </div>

        {eventos.length === 0 && (
          <div className="flex justify-center py-8 text-zinc-700">
            <p>Nenhum item encontrado</p>
          </div>
        )}

        <div className="space-y-16">
          {isLoading ? (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <RepriseSkeleton key={i} />
              ))}
            </div>
          ) : (
            categorias.map((cat, idx) => (
              <CategorySection
                key={cat}
                title={cat}
                index={idx}
                statusFilter={statusFilter}
                initialItems={eventos.filter(
                  (e) =>
                    (e.agrupador || "OUTROS_CONTEUDOS")
                      .trim()
                      .toUpperCase()
                      .replace(/\s+/g, "_") === cat
                )}
                searchTerm={debouncedSearch}
                onEdit={(item: any) => {
                  setEditingEvent(item)
                  setCurrentEditingEventId(item.idEvento)
                }}
                onSelect={(item: any) =>
                  setActivePlayback({
                    title: item.descricao,
                    links: [item.linkConteudo, item.linkSegundoConteudo].filter(Boolean),
                  })
                }
                onDelete={handleDelete}
              />
            ))
          )}
        </div>
      </PageMainContainer>

      <Sheet
        open={!!editingEvent}
        onOpenChange={() => setEditingEvent(null)}
      >
        <SheetContent
          side="right"
          className="w-full border-zinc-800 bg-zinc-950 p-0 text-white sm:max-w-md"
        >
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleFormSubmit)}
              className="flex h-full flex-col"
            >
              <SheetHeader className="border-b border-white/5 p-8 pb-6 text-left text-white">
                <SheetTitle className="text-2xl font-black uppercase italic">
                  {editingEvent?.id ? "Editar" : "Novo"} Conteúdo
                </SheetTitle>
                <SheetDescription className="text-[10px] font-bold uppercase italic tracking-widest text-orange-500">
                  Configurações
                </SheetDescription>
              </SheetHeader>

              <div className="custom-scrollbar flex-1 space-y-6 overflow-y-auto p-8">
                <FormField
                  control={form.control}
                  name="id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                        Evento
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-14 border-zinc-800 bg-zinc-900 text-white">
                            <SelectValue placeholder="Selecione o evento" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
                          {listaImersoes.map((imersao) => (
                            <SelectItem
                              key={imersao.id}
                              value={imersao.id.toString()}
                            >
                              {imersao.descricao}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                {/* NOVO SELETOR DE AGRUPADOR (TAG) */}
                <div
                  className="space-y-2"
                  ref={agrupadorRef}
                >
                  <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    Agrupador
                  </Label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsAgrupadorOpen(!isAgrupadorOpen)}
                      className="flex h-14 w-full items-center justify-between rounded-md border border-zinc-800 bg-zinc-900 px-4 text-xs text-zinc-300"
                    >
                      <span className="truncate">
                        {agrupadorInput
                          ? agrupadorInput.replace(/_/g, " ")
                          : "Selecione ou crie..."}
                      </span>
                      <ChevronsUpDown
                        size={16}
                        className="opacity-50"
                      />
                    </button>
                    <AnimatePresence>
                      {isAgrupadorOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute left-0 top-[60px] z-[100] w-full rounded-xl border border-zinc-800 bg-zinc-900 shadow-2xl"
                        >
                          <div className="flex items-center border-b border-white/5 p-3">
                            <Search
                              size={14}
                              className="mr-2 text-zinc-500"
                            />
                            <input
                              className="w-full bg-transparent text-sm outline-none"
                              placeholder="Buscar ou criar tag..."
                              value={searchAgrupador}
                              onChange={(e) => setSearchAgrupador(e.target.value)}
                              autoFocus
                            />
                          </div>
                          <div className="custom-scrollbar max-h-[200px] overflow-y-auto p-1">
                            {categorias
                              .filter((c) =>
                                c.toLowerCase().includes(searchAgrupador.toLowerCase())
                              )
                              .map((cat) => (
                                <button
                                  key={cat}
                                  type="button"
                                  onClick={() => {
                                    setAgrupadorInput(cat)
                                    setIsAgrupadorOpen(false)
                                  }}
                                  className="flex w-full items-center justify-between rounded-lg p-3 text-left text-xs text-zinc-400 hover:bg-white/5"
                                >
                                  {cat.replace(/_/g, " ")}
                                  {agrupadorInput === cat && (
                                    <Check
                                      size={14}
                                      className="text-orange-500"
                                    />
                                  )}
                                </button>
                              ))}
                            {searchAgrupador &&
                              !categorias.some(
                                (c) =>
                                  c.toLowerCase() ===
                                  searchAgrupador.toLowerCase().replace(/\s+/g, "_")
                              ) && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newValue = searchAgrupador
                                      .toUpperCase()
                                      .replace(/\s+/g, "_")
                                    setAgrupadorInput(newValue)
                                    setIsAgrupadorOpen(false)
                                  }}
                                  className="flex w-full p-3 text-xs font-bold italic text-orange-500 underline"
                                >
                                  Criar nova tag "{searchAgrupador}"
                                </button>
                              )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    Limite Exibição
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="DD/MM/YYYY"
                      className="h-14 border-zinc-800 bg-zinc-900 text-center font-mono text-white"
                      value={dateInput}
                      onChange={handleDateChange}
                    />
                    <Input
                      placeholder="HH:mm"
                      className="h-14 w-32 border-zinc-800 bg-zinc-900 text-center font-mono text-white"
                      value={timeInput}
                      onChange={handleTimeChange}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    Capa URL
                  </Label>
                  <Input
                    className="h-14 border-zinc-800 bg-zinc-900 text-white"
                    value={capaInput}
                    onChange={(e) => setCapaInput(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    Vídeo Principal
                  </Label>
                  <Input
                    className="h-14 border-zinc-800 bg-zinc-900 font-mono text-white"
                    value={videoInput}
                    onChange={(e) => setVideoInput(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    Vídeo Secundário (Opcional)
                  </Label>
                  <Input
                    className="h-14 border-zinc-800 bg-zinc-900 font-mono text-white"
                    value={videoSecundarioInput}
                    onChange={(e) => setVideoSecundarioInput(e.target.value)}
                  />
                </div>
              </div>

              <SheetFooter className="border-t border-white/5 p-8">
                <Button
                  type="submit"
                  disabled={isSaving || !isFormValid}
                  className="h-16 w-full bg-orange-600 font-black uppercase tracking-widest shadow-lg hover:bg-orange-500"
                >
                  {isSaving ? <Loader2 className="animate-spin" /> : "Salvar"}
                </Button>
              </SheetFooter>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      <AnimatePresence>
        {activePlayback && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[300] flex flex-col items-center justify-center bg-black/95 p-4 text-white backdrop-blur-2xl"
          >
            <button
              onClick={() => setActivePlayback(null)}
              className="absolute right-8 top-8 text-white/50 transition-colors hover:text-white"
            >
              <X size={40} />
            </button>
            <div className="w-full max-w-7xl space-y-8 text-center">
              <h2 className="text-3xl font-black uppercase italic leading-tight">
                {activePlayback.title}
              </h2>
              <div
                className={`grid gap-6 ${activePlayback.links.length > 1 ? "md:grid-cols-2" : "grid-cols-1"}`}
              >
                {activePlayback.links.map((link, idx) => (
                  <div
                    key={idx}
                    className="aspect-video overflow-hidden rounded-[32px] border border-white/10 bg-black shadow-2xl"
                  >
                    <iframe
                      src={getEmbedUrl(link)}
                      className="h-full w-full"
                      allowFullScreen
                    />
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const CategorySection = ({
  title,
  index,
  initialItems,
  searchTerm,
  statusFilter,
  onSelect,
  onEdit,
  onDelete,
}: {
  title: string
  index: number
  initialItems: Evento[]
  searchTerm: string
  statusFilter: "all" | "ativos" | "expirados"
  onSelect: (item: Evento) => void
  onEdit: (item: Evento) => void
  onDelete: (id: string | number) => void
}) => {
  const [items, setItems] = useState<Evento[]>(initialItems)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)

  const isWarRoom = title.toLowerCase().replace(/_/g, " ").trim() === "sala de guerra"

  const theme = useMemo(() => {
    if (isWarRoom) {
      return {
        border: "border-red-500/30",
        bg: "from-red-500/[0.06]",
        button: "border-red-500/20 text-red-500 hover:bg-red-500",
        accent: "text-red-500",
      }
    }
    return COLOR_SEQUENCE[index % COLOR_SEQUENCE.length]
  }, [isWarRoom, index])

  useEffect(() => {
    setItems(initialItems)
    setPage(1)
    setHasMore(true)
  }, [initialItems, searchTerm, statusFilter])

  const loadMore = async () => {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    try {
      const res = await getReprisesAction({
        page: page + 1,
        limit: 4,
        agrupador: title,
        status: statusFilter !== "all" ? statusFilter : undefined,
      })
      const newBatch = res.data.data || []
      if (newBatch.length === 0) setHasMore(false)
      else {
        setItems((prev) => {
          const loadedIds = new Set(prev.map((i: any) => i.idEvento))
          return [...prev, ...newBatch.filter((i: any) => !loadedIds.has(i.idEvento))]
        })
        setPage((p) => p + 1)
        setHasMore(!!res.data.meta.hasMore)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingMore(false)
    }
  }

  const filtered = items.filter((p: any) => {
    const isExpired = p.limiteExibicao ? isPast(new Date(p.limiteExibicao)) : false
    const matchesStatus =
      statusFilter === "all" || (statusFilter === "ativos" ? !isExpired : isExpired)
    return matchesStatus
  })

  if (filtered.length === 0) {
    return (
      <div className="flex justify-center rounded-lg bg-zinc-900 py-8">
        <p>Nenhum item encontrado</p>
      </div>
    )
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`relative rounded-[40px] border ${theme.border} bg-gradient-to-br ${theme.bg} to-transparent p-8 md:p-12`}
    >
      <div className="mb-10 flex items-center justify-between">
        <h2 className="flex items-center gap-3 text-3xl font-black uppercase italic leading-tight text-white">
          {isWarRoom && (
            <span className="h-3 w-3 animate-pulse rounded-full bg-red-500 shadow-[0_0_10px_red]" />
          )}
          {title.replace(/_/g, " ")}
        </h2>
        <span className="text-[10px] font-black uppercase tracking-widest text-zinc-600">
          {filtered.length} Itens
        </span>
      </div>
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filtered.map((item: any, idx: number) => (
          <RepriseCard
            key={item.id}
            item={item}
            index={idx}
            onOpen={() => onSelect(item)}
            onEdit={onEdit}
            onDelete={() => onDelete(item.idEvento)}
          />
        ))}
      </div>
      {hasMore && (
        <div className="mt-12 flex justify-center">
          <Button
            variant="outline"
            onClick={loadMore}
            disabled={loadingMore}
            className={`h-14 rounded-full bg-transparent px-10 text-[11px] font-black uppercase tracking-widest transition-all hover:text-black ${theme.button}`}
          >
            {loadingMore ? <Loader2 className="mr-2 animate-spin" /> : "Carregar mais"}
          </Button>
        </div>
      )}
    </motion.section>
  )
}
